import React, { useEffect, useState, useCallback } from 'react';
import { useAtom } from 'jotai';
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { selectedClientIdAtom } from '@/state/clients';
import { TokenService } from '@/services/auth/tokenService';
import { createAuthenticatedRequest } from './axiosApi';

interface LiveKitStreamProps {
  wsUrl: string;
  clientId: string;
  identity: string;
  roomName: string;
}

const RoomViewInsideRoom = () => {
  const tracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
    { source: Track.Source.Microphone, withPlaceholder: true },
  ]);

  if (!tracks?.length) {
    return <div>Connecting…</div>;
  }

  // Add deduplication by participant identity
  const uniqueParticipantIds = new Set();
  const uniqueTracks = tracks.filter((track) => {
    if (
      track.participant.name &&
      !track.participant.name.startsWith('Camera') &&
      !track.participant.name.startsWith('Microphone')
    ) {
      return false;
    }
    if (uniqueParticipantIds.has(track.participant.identity)) {
      return false;
    }
    uniqueParticipantIds.add(track.participant.identity);
    return true;
  });

  return (
    <GridLayout tracks={uniqueTracks} className="lk-grid">
      <ParticipantTile />
    </GridLayout>
  );
};

const LiveKitStream: React.FC<LiveKitStreamProps> = ({
  wsUrl,
  clientId,
  identity,
  roomName,
}) => {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClientId] = useAtom(selectedClientIdAtom);
  // Simple token fetcher
  const fetchLiveKitToken = useCallback(async () => {
    try {
      console.log(`Fetching token for room: ${roomName}`);
      setError(null);
      setIsLoading(true);

      const tokenService = TokenService.getInstance();
      const authToken = await tokenService.getCurrentToken();

      if (!authToken || !selectedClientId) {
        return [];
      }
      const apiUrl = process.env.PUBLIC_API_URL || 'http://localhost:8080';
      const axios = await createAuthenticatedRequest(apiUrl, authToken);
      const response = await axios.get('/livekit/token', {
        params: { client_id: clientId, room: roomName, identity },
      });

      setToken(response.data.token);
      return response.data.token;
    } catch (err) {
      console.error('Failed to fetch token:', err);
      setError('Failed to fetch authentication token');
      setToken(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [roomName, selectedClientId, clientId, identity]);

  // Fetch token whenever room connection params change
  useEffect(() => {
    if (!wsUrl) {
      setError('No LiveKit URL configured');
      setIsLoading(false);
      return;
    }

    // Add small delay to ensure previous room is fully disconnected
    const timeoutId = setTimeout(() => {
      setToken(null);
      fetchLiveKitToken();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [wsUrl, clientId, roomName, identity, fetchLiveKitToken]);

  // Retry function for manual reconnection

  // Loading state
  if (isLoading) {
    return <div style={{ color: '#ccc' }}>Loading LiveKit stream…</div>;
  }

  // Error state
  if (error) {
    return (
      <div style={{ color: '#ff6b6b', padding: '20px', textAlign: 'center' }}>
        <div>Error: {error}</div>
      </div>
    );
  }

  // No token yet
  if (!token) {
    return <div style={{ color: '#ccc' }}>Connecting…</div>;
  }

  // Render LiveKit room - let LiveKitRoom handle all connection logic
  return (
    <div className="livekit-container" style={{ backgroundColor: '#000' }}>
      <LiveKitRoom
        token={token}
        serverUrl={wsUrl}
        // Key prop forces remount when room params change
        key={`${roomName}-${identity}-${clientId}`}
        connectOptions={{
          autoSubscribe: true,
        }}
        onError={(error) => {
          if (error.message.includes('CLIENT_INITIATED')) {
            console.log(
              'Client initiated disconnect - this is expected during room switches',
            );
          } else {
            console.log('LiveKit room error:', error);
          }
          // Only set error if it's not a disconnect during room switching
          // if (!error.message.includes("CLIENT_INITIATED")) {
          //   setError(`Room connection error: ${error.message}`);
          // }
        }}
        onConnected={() => {
          console.log(`Connected to room: ${roomName}`);
          setError(null); // Clear any previous errors on successful connection
        }}
        onDisconnected={(reason) => {
          console.log(`Disconnected from room: ${roomName}, reason:`, reason);
          // Don't treat client-initiated disconnects as errors
          if (reason && reason.toString().includes('CLIENT_INITIATED')) {
            console.log(
              'Client initiated disconnect - this is expected during room switches',
            );
          }
        }}
      >
        <RoomAudioRenderer />
        <RoomViewInsideRoom />
      </LiveKitRoom>
    </div>
  );
};

export default LiveKitStream;
